/**
 * synkro setup-github — interactive setup for PR scanning.
 *
 * Flow:
 *   1. Ensure user is logged in (has JWT + user_id + org_id in ~/.synkro/credentials.json).
 *   2. Check if GitHub is connected via WorkOS Pipes.
 *   3. If not, get Pipes OAuth URL and open browser for the user to authorize.
 *   4. Poll until GitHub connection is established.
 *   5. Run `claude setup-token` to get Claude Code OAuth token.
 *   6. List accessible repos via GitHub API (using Pipes token).
 *   7. Interactive multi-select.
 *   8. Push secrets (SYNKRO_API_KEY + CLAUDE_CODE_OAUTH_TOKEN) to each repo.
 *   9. Write .github/workflows/synkro.yml to the local repo if cwd is one.
 */
import { createInterface } from 'node:readline/promises';
import { stdin as input, stdout as output } from 'node:process';
import { execSync } from 'node:child_process';
import { existsSync, readFileSync } from 'node:fs';
import { homedir, platform } from 'node:os';
import { join } from 'node:path';
import { execFile } from 'node:child_process';
import {
  listAccessibleRepos,
  pushSecretsToRepo,
  writeWorkflowFile,
  findGitRoot,
  SECRET_NAMES,
  WORKFLOW_RELATIVE_PATH,
} from '../installer/githubSetup.js';
import { isAuthenticated, getAccessToken, getUserInfo } from '../auth/stub.js';
import { captureClaudeSetupToken } from '../local-cc/setupToken.js';

const SYNKRO_DIR = join(homedir(), '.synkro');
const CONFIG_PATH = join(SYNKRO_DIR, 'config.env');

function readConfig(): Record<string, string> {
  if (!existsSync(CONFIG_PATH)) return {};
  const out: Record<string, string> = {};
  for (const line of readFileSync(CONFIG_PATH, 'utf-8').split('\n')) {
    const t = line.trim();
    if (!t || t.startsWith('#')) continue;
    const eq = t.indexOf('=');
    if (eq > 0) out[t.slice(0, eq).trim()] = t.slice(eq + 1).trim().replace(/^['"]|['"]$/g, '');
  }
  return out;
}

async function prompt(rl: ReturnType<typeof createInterface>, q: string, opts: { silent?: boolean } = {}): Promise<string> {
  if (opts.silent) {
    process.stdout.write(q);
    const wasRaw = (process.stdin as any).isRaw;
    if ((process.stdin as any).setRawMode) (process.stdin as any).setRawMode(true);
    return await new Promise<string>((resolve) => {
      let chunk = '';
      const onData = (data: Buffer) => {
        const s = data.toString('utf-8');
        if (s === '\r' || s === '\n' || s === '\r\n') {
          process.stdin.removeListener('data', onData);
          if ((process.stdin as any).setRawMode) (process.stdin as any).setRawMode(wasRaw ?? false);
          process.stdout.write('\n');
          resolve(chunk);
          return;
        }
        if (s === '') process.exit(130);
        if (s === '' || s === '\b') { chunk = chunk.slice(0, -1); return; }
        chunk += s;
      };
      process.stdin.on('data', onData);
    });
  }
  return await rl.question(q);
}

function openBrowser(url: string): void {
  const os = platform();
  let bin: string;
  let args: string[];
  switch (os) {
    case 'darwin': bin = 'open'; args = [url]; break;
    case 'win32': bin = 'cmd'; args = ['/c', 'start', '', url]; break;
    default: bin = 'xdg-open'; args = [url]; break;
  }
  execFile(bin, args, () => {});
}

function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

async function apiCall<T = any>(gatewayUrl: string, jwt: string, path: string, opts: RequestInit = {}): Promise<T> {
  const resp = await fetch(`${gatewayUrl}${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`API ${resp.status}: ${text.slice(0, 200)}`);
  }
  return resp.json() as Promise<T>;
}

/**
 * Connect GitHub via WorkOS Pipes OAuth. Returns the token if connected, null if user
 * declines or times out. Exported so `install` can embed this in its flow.
 */
export async function connectGitHub(gatewayUrl: string, jwt: string, opts: { silent?: boolean } = {}): Promise<string | null> {
  // Check if already connected
  try {
    const result = await apiCall<{ connected: boolean; token?: string }>(
      gatewayUrl, jwt, '/api/v1/cli/github-token',
    );
    if (result.connected && result.token) {
      if (!opts.silent) console.log('  ✓ GitHub already connected via Synkro.');
      return result.token;
    }
  } catch {}

  // Get Pipes OAuth URL and open browser
  if (!opts.silent) console.log('  Opening browser to authorize GitHub...');
  try {
    const authResp = await apiCall<{ url: string }>(
      gatewayUrl, jwt, '/api/pipes-widget/authorize/github', { method: 'POST', body: '{}' },
    );
    openBrowser(authResp.url);
    if (!opts.silent) console.log('  Waiting for authorization...');
  } catch (err) {
    if (!opts.silent) console.error(`  Failed to start GitHub authorization: ${(err as Error).message}`);
    return null;
  }

  // Poll until connected (max 2 minutes)
  const deadline = Date.now() + 120_000;
  while (Date.now() < deadline) {
    await sleep(2000);
    try {
      const result = await apiCall<{ connected: boolean; token?: string }>(
        gatewayUrl, jwt, '/api/v1/cli/github-token',
      );
      if (result.connected && result.token) {
        if (!opts.silent) console.log('\n  ✓ GitHub connected!');
        return result.token;
      }
    } catch {}
    if (!opts.silent) process.stdout.write('.');
  }
  if (!opts.silent) console.error('\n  Timed out waiting for GitHub authorization.');
  return null;
}

export interface SetupGithubOptions {
  nonInteractive?: boolean;
  githubToken?: string;
  skipClaudeToken?: boolean;
}

export async function setupGithubCommand(opts: SetupGithubOptions = {}): Promise<void> {
  if (!isAuthenticated()) {
    console.error('Not authenticated. Run `synkro-cli login` first.');
    process.exit(1);
  }
  const config = readConfig();
  const gatewayUrl = (config.SYNKRO_GATEWAY_URL || process.env.SYNKRO_GATEWAY_URL || 'https://api.synkro.sh').replace(/\/$/, '');
  const jwt = getAccessToken();
  if (!jwt) {
    console.error('Could not load access token from ~/.synkro/credentials.json. Run `synkro-cli login`.');
    process.exit(1);
  }

  // ── 1. Mint CI API key ──────────────────────────────────────────────
  console.log('Requesting CI API key from Synkro...');
  let synkroCiApiKey: string;
  try {
    const minted = await apiCall<{ api_key: string; expires_at: string }>(
      gatewayUrl, jwt, '/api/v1/cli/ci-api-key', { method: 'POST', body: '{}' },
    );
    synkroCiApiKey = minted.api_key;
    console.log(`  ✓ Issued CI key (${synkroCiApiKey.slice(0, 18)}…), expires ${minted.expires_at.slice(0, 10)}`);
  } catch (err) {
    console.error(`Failed to mint CI API key: ${(err as Error).message}`);
    process.exit(1);
  }

  // ── 2. Get GitHub token via WorkOS Pipes ────────────────────────────
  let ghToken: string;

  if (opts.githubToken) {
    ghToken = opts.githubToken;
  } else if (opts.nonInteractive) {
    // In non-interactive mode (CI), try Pipes first, fall back to gh CLI
    try {
      const result = await apiCall<{ connected: boolean; token?: string }>(
        gatewayUrl, jwt, '/api/v1/cli/github-token',
      );
      if (result.connected && result.token) {
        ghToken = result.token;
      } else {
        throw new Error('not connected');
      }
    } catch {
      try {
        ghToken = execSync('gh auth token', { encoding: 'utf-8', timeout: 5000 }).trim();
      } catch {
        console.error('GitHub not connected. Run `synkro-cli setup-github` interactively to connect.');
        return;
      }
    }
  } else {
    // Interactive mode — use Pipes OAuth
    console.log('\nConnecting to GitHub...');
    const token = await connectGitHub(gatewayUrl, jwt);
    if (!token) {
      console.error('GitHub connection failed. Try again.');
      process.exit(1);
    }
    ghToken = token;
    console.log();
  }

  // ── 3. Claude Code OAuth token ──────────────────────────────────────
  let claudeToken: string | undefined;
  if (!opts.skipClaudeToken) {
    console.log('Generating Claude Code OAuth token...');
    console.log('  A browser window will open — authorize with your Claude account.\n');
    try {
      claudeToken = await captureClaudeSetupToken();
    } catch (err) {
      console.error(`Failed to get Claude token: ${err instanceof Error ? err.message : String(err)}`);
      if (opts.nonInteractive) return;
      process.exit(1);
    }
    if (!claudeToken.startsWith('sk-ant-oat01-')) {
      console.error('Invalid token received from `claude setup-token`. Expected sk-ant-oat01-...');
      if (opts.nonInteractive) return;
      process.exit(1);
    }
    console.log('  Validating token...');
    try {
      const validateResult = execSync(
        'claude --print --output-format json "say ok"',
        { env: { ...process.env, CLAUDE_CODE_OAUTH_TOKEN: claudeToken }, encoding: 'utf-8', timeout: 30_000, stdio: ['ignore', 'pipe', 'pipe'] },
      );
      const result = JSON.parse(validateResult);
      if (result.is_error) throw new Error(result.result || 'auth failed');
      console.log('  ✓ Token validated.\n');
    } catch (err) {
      console.error(`Token validation failed: ${err instanceof Error ? err.message : String(err)}`);
      if (opts.nonInteractive) return;
      process.exit(1);
    }
  }

  // ── 4. Select repos ─────────────────────────────────────────────────
  let selected: Array<{ owner: string; repo: string; full_name: string }>;
  if (opts.nonInteractive) {
    let currentFullName: string | null = null;
    try {
      const remoteUrl = execSync('git remote get-url origin', { encoding: 'utf-8', timeout: 5000 }).trim();
      const m = remoteUrl.match(/(?:github\.com)[:/](.+?)(?:\.git)?$/);
      if (m) currentFullName = m[1];
    } catch {}
    if (!currentFullName) {
      console.warn('  ⚠ Not in a GitHub repo. Skipping PR scan setup.');
      return;
    }
    const [owner, repo] = currentFullName.split('/');
    selected = [{ owner, repo, full_name: currentFullName }];
    console.log(`  Auto-selected repo: ${currentFullName}`);
  } else {
    console.log('Fetching accessible repos...');
    const repos = await listAccessibleRepos({ token: ghToken! });
    if (repos.length === 0) {
      console.error('No accessible repos found. Check your GitHub permissions.');
      process.exit(1);
    }
    console.log(`\nFound ${repos.length} accessible repo(s):\n`);
    repos.slice(0, 100).forEach((r, i) => {
      console.log(`  ${String(i + 1).padStart(3)}.  ${r.full_name}`);
    });
    console.log();
    const rl2 = createInterface({ input, output });
    const selectionRaw = await prompt(rl2, 'Select repos to enable (comma-separated numbers, e.g. 1,3,5): ');
    const selectedIdx = selectionRaw
      .split(',')
      .map((s) => parseInt(s.trim(), 10) - 1)
      .filter((n) => !isNaN(n) && n >= 0 && n < repos.length);
    if (selectedIdx.length === 0) {
      console.error('No valid selections.');
      rl2.close();
      process.exit(1);
    }
    selected = selectedIdx.map((i) => repos[i]);
    console.log(`\nWill push secrets to ${selected.length} repo(s):`);
    for (const r of selected) console.log(`  • ${r.full_name}`);
    console.log();
    const confirm = (await prompt(rl2, 'Continue? (yes/no): ')).trim().toLowerCase();
    if (confirm !== 'yes' && confirm !== 'y') {
      console.log('Cancelled.');
      rl2.close();
      process.exit(0);
    }
    rl2.close();
  }

  // ── 5. Push secrets ─────────────────────────────────────────────────
  console.log();
  for (const r of selected) {
    process.stdout.write(`Pushing secrets to ${r.full_name}... `);
    try {
      await pushSecretsToRepo(
        { token: ghToken! },
        r.owner,
        r.repo,
        {
          claudeCodeOauthToken: claudeToken,
          synkroApiKey: synkroCiApiKey,
        },
      );
      console.log('✓');
    } catch (err) {
      console.log(`✗ (${(err as Error).message})`);
    }
  }

  // ── 6. Write workflow file ──────────────────────────────────────────
  console.log();
  const gitRoot = findGitRoot(process.cwd());
  if (gitRoot) {
    const written = writeWorkflowFile(gitRoot);
    if (written) {
      console.log(`Wrote workflow: ${written}`);
      console.log('Commit and push it to enable PR scanning.');
    }
  } else {
    console.log('Not in a git repo. To enable scanning, add this file to your repo:');
    console.log(`  Path: ${WORKFLOW_RELATIVE_PATH}`);
    console.log(`  Content: run \`synkro-cli setup-github\` from inside a repo to write it automatically`);
  }

  console.log();
  console.log('✓ PR scan setup complete.');
  console.log(`Secrets pushed: ${SECRET_NAMES.CLAUDE_OAUTH}, ${SECRET_NAMES.SYNKRO_API_KEY}`);
  console.log('Open a PR on any selected repo to trigger your first Synkro scan.');
}
